import { useState } from 'react';
import SubLayout from '../common/subLayout';

const PricingContent = () => {
  const [activeTab, setActiveTab] = useState<'sell' | 'buy'>('sell');
  
  const handleWhatsAppContact = () => {
    const message = "Hello GJC Recycling, I'd like to get current pricing information.";
    window.open(`https://wa.me/2348125020603?text=${encodeURIComponent(message)}`, '_blank');
  };

  const buyMaterials = [
    { material: "PET Bottles", note: "Clean, sorted bottles" },
    { material: "HDPE (Natural)", note: "Natural color preferred" },
    { material: "HDPE (Color)", note: "Mixed colors accepted" },
    { material: "LDPE", note: "Clean film and bags" },
    { material: "PP", note: "Rigid and film forms" },
  ];

  const sellMaterials = [
    { material: "Cold Wash PET Flakes", note: "Textile & food grade" },
    { material: "Palletised HDPE", note: "Industrial grade" },
    { material: "Palletised LDPE", note: "Film & packaging" },
    { material: "Palletised PP", note: "Automotive grade" },
    { material: "Plastic Spoons", note: "Food grade" },
    { material: "Plastic Packers", note: "Food grade" },
  ];

  return (
    <div className="container mx-auto px-4 max-w-5xl py-8">
      {/* Tab Switcher */}
      <div className="flex justify-center mb-8">
        <div className="bg-gray-100 p-1 rounded-lg inline-flex shadow-md">
          <button
            onClick={() => setActiveTab('sell')}
            className={`px-6 py-2.5 rounded-lg font-semibold transition-all duration-300 ${
              activeTab === 'sell' 
                ? 'bg-green-600 text-white shadow-lg' 
                : 'text-gray-600 hover:bg-gray-200'
            }`}
          >
             We Buy Waste
          </button>
          <button
            onClick={() => setActiveTab('buy')}
            className={`px-6 py-2.5 rounded-lg font-semibold transition-all duration-300 ${
              activeTab === 'buy' 
                ? 'bg-blue-600 text-white shadow-lg' 
                : 'text-gray-600 hover:bg-gray-200'
            }`}
          >
             We Sell Products
          </button>
        </div>
      </div>
      
      {/* Pricing Cards */}
      <div className="bg-gray-50 rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100">
        {activeTab === 'sell' ? (
          <>
            <div className="flex items-center gap-2 mb-6">
              <span className="text-2xl"></span>
              <h3 className="text-xl font-bold text-gray-800">We Buy These Waste Materials</h3>
            </div>
            <div className="space-y-3">
              {buyMaterials.map((item, index) => (
                <div key={index} className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-all border border-gray-100">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div>
                      <span className="font-semibold text-gray-800">{item.material}</span>
                      <span className="text-sm text-gray-500 ml-2">• {item.note}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-400">Contact for pricing</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-gray-700">
              <p className="font-semibold flex items-center gap-2">📌 Important Notes:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                <li>Minimum quantity: 50kg per collection</li>
                <li>Free pickup available in Ibadan for bulk quantities</li>
                <li>Prices vary based on quality and quantity</li>
                <li>Payment made immediately upon collection</li>
              </ul>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-6">
              <span className="text-2xl"></span>
              <h3 className="text-xl font-bold text-gray-800">We Sell These Products</h3>
            </div>
            <div className="space-y-3">
              {sellMaterials.map((item, index) => (
                <div key={index} className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-all border border-gray-100">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div>
                      <span className="font-semibold text-gray-800">{item.material}</span>
                      <span className="text-sm text-gray-500 ml-2">• {item.note}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-400">Contact for pricing</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-gray-700">
              <p className="font-semibold flex items-center gap-2">📌 Important Notes:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                <li>Bulk discounts available for orders above 500kg</li>
                <li>Nationwide delivery available</li>
                <li>Custom orders and specifications welcome</li>
                <li>All products meet quality standards</li>
              </ul>
            </div>
          </>
        )}
        
        {/* Contact CTA */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-4">
            Prices are subject to market changes. Contact us for the most current rates and custom quotes.
          </p>
          <button
            onClick={handleWhatsAppContact}
            className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-bold transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center gap-2 mx-auto"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
             Get Current Pricing on WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
};

const PricingSection = (): JSX.Element => {
  return (
    <SubLayout
      _style2="my-20"
      subText="We believe in transparent pricing for both our waste suppliers and product buyers. Contact us for bulk discounts and custom quotes."
      text="Transparent Pricing"
    >
      <PricingContent />
    </SubLayout>
  );
};

export default PricingSection;